/**
 * Created by Ning Jiang on 1/8/2020.
 */

import {Button, ButtonLike, ButtonState} from "@nolimitcity/slot-game/bin/core/component/Button";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IPickAndClickViewConfig, PickAndClickView} from "@nolimitcity/slot-game/bin/core/pickandclick/PickAndClickView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ParsedInitData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedInitData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite, TweenLite} from "gsap";
import {DeadwoodSoundConfig} from "../../DeadwoodSoundConfig";
import {DeadwoodGameMode} from "../../gamemode/DeadwoodGameMode";
import {DeadwoodMultiplierController} from "../../multiplier/DeadwoodMultiplierController";
import {IDeadwoodParsedServerData} from "../../server/data/IDeadwoodParsedServerData";
import {PickButton} from "./PickButton";

export class DeadwoodPickNClickIntroView extends PickAndClickView {

    private readonly _buttonNames:string[] = ["GUNSLINGER", "HUNTER"];
    private readonly _featureTextStrings:string[] = ["STICKY MULTIPLIER", "GUARANTEED WILD"];
    private readonly _featureTextStyle:TextStyleOptions = {
        fontFamily : "Open Sans",
        fontSize : 30,
        fill : 0xffffff,
        // fontStyle : FontStyle.ITALIC,
        fontWeight : FontWeight.BOLD,
        wordWrap : false
    };
    private readonly _volatilityTextStyle:any = {
        fill : 0xffffff,
        fontFamily : "Open Sans",
        wordWrap : false,
        fontWeight : FontWeight.BOLD,
        fontSize : 22
    };

    private _featureTexts:PIXI.Text[];
    private _volatilityBar:PIXI.Container;
    private _highVLogo:PIXI.Sprite;
    private _extremeVLogo:PIXI.Sprite;

    private _featureTextMinScale:number = 1;

    constructor(config:IPickAndClickViewConfig) {
        super(config, false);
        this._featureTexts = [];
    }

    protected createBackground():PIXI.DisplayObject {
        return new PIXI.Graphics();
    }

    protected onResizeBackground(data:IResizeData):void {
        const bg:PIXI.Graphics = <PIXI.Graphics>this._background;
        bg.clear();
        bg.beginFill(0x000000, 0.85);
        bg.drawRect(0, 0, this._screenWidth, this._screenHeight)
    }

    protected createGameGraphics():PIXI.DisplayObject {
        return new PIXI.Container();
    }

    protected onResizeGameGraphics(resizeData:IResizeData):void {
        this.resizeVolatilityBar();
    }

    private resizeVolatilityBar():void {
        if(this._volatilityBar) {
            this._volatilityBar.x = this._screenWidth * 0.5;
            this._volatilityBar.y = this._buttons[0].y + 360;

            if(StageManager.orientation === Orientation.PORTRAIT) {
                this._highVLogo.position.set(-200, 40);
                this._extremeVLogo.position.set(200, 40);
            } else {
                this._highVLogo.position.set(-410, -15);
                this._extremeVLogo.position.set(430, -15);
            }
        }
    }

    protected createButton(index:number, selectedIndex:number, clickCallback:(i:number, auto:boolean) => void):Button {
        const keyword:string = this._buttonNames[index];
        const button:PickButton = new PickButton(keyword,
            () => {
                this.playClick(index);
                clickCallback(selectedIndex, this._autoClicked);
            });
        button.enabled = false;

        const featureText:PIXI.Text = new PIXI.Text(Translation.translate(this._featureTextStrings[index]), this._featureTextStyle);
        featureText.anchor.set(0.5);
        featureText.alpha = 0;
        this.addChild(featureText);
        this._featureTexts.push(featureText);
        if(featureText.width > 330) {
            this._featureTextMinScale = Math.min(this._featureTextMinScale, MathHelper.roundToDecimals(330 / featureText.width, 2));
        }

        this.addChild(button);
        return button;
    }

    private playClick(index:number):void {
        const buttonBounce:TimelineLite = new TimelineLite();
        buttonBounce.to(this._buttons[index].scale, 0.06, {x : 0.8, y : 0.8})
            .to(this._buttons[index].scale, 0.1, {x : 1.1, y : 1.1});
        buttonBounce.add(() => SlotGame.sound.playAmbience(DeadwoodSoundConfig.instance.FS_AMBIANCE), 0.751);
        (<PickButton>this._buttons[index]).playClick();
        SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.FS_SELECT);
    }

    private playUnselected(index:number):void {
        TweenLite.to(this._buttons[index].scale, 0.15, {x : 0.9, y : 0.9});
        (<PickButton>this._buttons[index]).stopIdleGlow();
    }

    protected onResizeButtons(resizeData:IResizeData):void {
        this._buttons.forEach((button:PIXI.DisplayObject, index:number) => {
            button = <PickButton>button;
            button.position.set(NolimitApplication.screenBounds.width * (0.75 - 0.5 * index) / this._layer.scale.x, NolimitApplication.screenBounds.height * 0.4 / this._layer.scale.y);
            this._featureTexts[index].position.set(button.x, button.y + 260);
            this._featureTexts[index].scale.set(this._featureTextMinScale);
        });
    }

    protected startPresentation(data?:IDeadwoodParsedServerData, onShowComplete?:() => void):void {
        this._buttons.forEach((child:PIXI.DisplayObject) => {
            const button = <PickButton>child;
            button.initGlow();
            button.setShootOut(data!.fsSuperModeActive);
            button.scale.set(1);
            button.alpha = 0;
        });

        switch(data!.nextMode) {
            case DeadwoodGameMode.FREESPIN_PICK:
                (<PickButton>this._buttons[0]).setFSNumber(data!.numberOfSpinsFreespin[0], true);
                (<PickButton>this._buttons[1]).setFSNumber(data!.numberOfSpinsFreespin[1], true);
                break;
            case DeadwoodGameMode.FREESPIN_GUNSLINGER :
                (<PickButton>this._buttons[0]).setFSNumber(data!.freespinsLeft, true);
                (<PickButton>this._buttons[1]).setFSNumber(data!.freespinsLeft, false);
                break;
            case DeadwoodGameMode.FREESPIN_HUNTERS:
                (<PickButton>this._buttons[0]).setFSNumber(data!.freespinsLeft, false);
                (<PickButton>this._buttons[1]).setFSNumber(data!.freespinsLeft, true);
                break;
            default:
                (<PickButton>this._buttons[0]).setFSNumber(-1, false);
                (<PickButton>this._buttons[1]).setFSNumber(-1, false);
        }

        if(!this._volatilityBar) {
            this._volatilityBar = new PIXI.Container();
            this.addChild(this._volatilityBar);

            const bar:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("volatilityBar")[0]);
            bar.anchor.set(0.5);
            this._volatilityBar.addChild(bar);

            let textMinScale:number = 1;
            const text1:PIXI.Text = new PIXI.Text(Translation.translate("High risk, high potential"), this._volatilityTextStyle);
            text1.anchor.set(1, 1);
            text1.position.set(320, -12);
            this._volatilityBar.addChild(text1);
            if(text1.width > 300) {
                textMinScale = Math.min(textMinScale, MathHelper.roundToDecimals(300 / text1.width, 2));
            }

            const text2:PIXI.Text = new PIXI.Text(Translation.translate("Win more often"), this._volatilityTextStyle);
            text2.anchor.set(0, 1);
            text2.position.set(-290, -12);
            this._volatilityBar.addChild(text2);
            if(text2.width > 300) {
                textMinScale = Math.min(textMinScale, MathHelper.roundToDecimals(300 / text2.width, 2));
            }

            if(textMinScale < 1) {
                text1.scale.set(textMinScale);
                text2.scale.set(textMinScale);
            }

            this._highVLogo = new PIXI.Sprite(GameResources.getTextures("volatilityHigh")[0]);
            this._highVLogo.anchor.set(0.5);
            this._volatilityBar.addChild(this._highVLogo);

            this._extremeVLogo = new PIXI.Sprite(GameResources.getTextures("volatilityExtreme")[0]);
            this._extremeVLogo.anchor.set(0.5);
            this._volatilityBar.addChild(this._extremeVLogo);

            this.resizeVolatilityBar();
        }
        this._volatilityBar.alpha = 0;

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            TweenLite.fromTo(this._background, 0.5, {alpha : 0}, {alpha : 1}),
            TweenLite.fromTo(this._buttons[0], 0.5, {alpha : 0}, {alpha : 1, delay : 0.3}),
            TweenLite.fromTo(this._buttons[1], 0.5, {alpha : 0}, {alpha : 1, delay : 0.3}),
            TweenLite.fromTo(this._featureTexts[0], 0.5, {alpha : 0}, {alpha : 1, delay : 0.3}),
            TweenLite.fromTo(this._featureTexts[1], 0.5, {alpha : 0}, {alpha : 1, delay : 0.3}),
            TweenLite.fromTo(this._volatilityBar, 0.5, {alpha : 0}, {alpha : 1, delay : 0.5})
        ]);
        timeline.add(() => super.startPresentation(data, onShowComplete));
    }

    public wait():void {
    }

    protected stopPresentation(data?:IDeadwoodParsedServerData, closeCallback?:() => void):void {
        const selectedIndex:number = data!.playerFSSelection - 1;
        const unselectedIndex:number = selectedIndex ^ 1;
        if(selectedIndex < 0 || selectedIndex > 1) {
            debugger;
            throw new Error(`DeadwoodPickNClickIntroView.stopPresentation(): illegal data.playerFSSelection!`);
        }
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                (<PickButton>this._buttons[selectedIndex]).setState(ButtonState.idle);
                (<PickButton>this._buttons[selectedIndex]).playIdleGlow(true);
                const isRestoreSelected:boolean = data instanceof ParsedInitData && data.isRestoreState && (data!.nextMode === DeadwoodGameMode.FREESPIN_HUNTERS || data!.nextMode === DeadwoodGameMode.FREESPIN_GUNSLINGER);
                if(isRestoreSelected) {
                    this.playClick(selectedIndex);
                }
                this.playUnselected(unselectedIndex);
            }
        ]);
        timeline.add((<PickButton>this._buttons[unselectedIndex]).playUnselectedTint(this._featureTexts[unselectedIndex]));
        timeline.add("close", "+=1");
        timeline.add([
            TweenLite.to(this._background, 1, {alpha : 0}),
            TweenLite.to(this._buttons[0], selectedIndex === 0 ? 0.8 : 0.5, {alpha : 0}),
            TweenLite.to(this._buttons[1], selectedIndex === 1 ? 0.8 : 0.5, {alpha : 0}),
            TweenLite.to(this._featureTexts[0], selectedIndex === 0 ? 0.8 : 0.5, {alpha : 0}),
            TweenLite.to(this._featureTexts[1], selectedIndex === 1 ? 0.8 : 0.5, {alpha : 0}),
            TweenLite.to(this._volatilityBar, 0.5, {alpha : 0})
        ], "close");
        timeline.add(() => {
            (<PickButton>this._buttons[unselectedIndex]).resetUnselectedTint(this._featureTexts[unselectedIndex]);
            (<PickButton>this._buttons[0]).setState(ButtonState.disabled);
            (<PickButton>this._buttons[1]).setState(ButtonState.disabled);
        });
        let daggerDelay:number = 0;
        if(selectedIndex === 0) {
            daggerDelay = 0.2;
            timeline.add(() => EventHandler.dispatchEvent(new GameEvent(DeadwoodMultiplierController.SHOW_DAGGER)));
        }
        timeline.add(() => super.stopPresentation(data, closeCallback), `+=${daggerDelay}`);
    }
}